import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IonPage, useIonViewDidEnter } from '@ionic/react';
import { RouteComponentProps } from 'react-router-dom';
import { authStateSelector, signUpEmailSelector } from '../../../redux/auth/selector';
import { updateSignUpData } from '../../../redux/auth/reducer';
import { PrivateRoutes, PublicRoutes } from '../../../routes/navigation';
import { TranslatedSelectWithSearchFormFeature } from '../../../translated-components';
import i18nCountries from 'i18n-iso-countries';
import {
  useDefaultCountriesByLang,
  useAnalytics,
  useAuthFlow,
  AuthProvidersEnum,
  useLog,
  USAStates,
} from '../../../shared';
import { AuthPageHeaderFeature } from 'src/features/AuthPageHeaderFeature';
import { loadUserThunk } from 'src/redux/user/thunk';

const CountryPage: FC<RouteComponentProps> = ({ history }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('auth');
  const dispatch = useDispatch();
  const defaultCountries = useDefaultCountriesByLang();
  const { trackPredefinedEvent } = useAnalytics();

  const nameRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const signUpEmail = useSelector(signUpEmailSelector);
  const authState = useSelector(authStateSelector);
  const { linkUser, authUser } = useAuthFlow();

  const isUserAuthWithEmail = useMemo(
    () => authUser?.email && authUser?.providerData.find((provider: any) => provider.providerId === 'password'),
    [authUser]
  );
  const { logError } = useLog();

  useIonViewDidEnter(() => {
    if (!signUpEmail) {
      const webToWebAuth = localStorage.getItem('webToWebAuth');

      if (webToWebAuth) {
        history.push(PublicRoutes.AUTH);
      } else {
        history.push(PublicRoutes.EMAIL_SIGN_IN);
      }
      return;
    }
  }, [signUpEmail, history]);

  useIonViewDidEnter(async () => {
    trackPredefinedEvent('SIGNUP_USER_INFO_VIEWED', {
      screen_type: 'country',
    });
  });

  useIonViewDidEnter(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, [nameRef]);

  const handleCountrySubmit = useCallback(
    async (countryOrState: string) => {
      trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
        screen_type: 'country',
        value: countryOrState,
      });

      if (language === 'en' && USAStates?.includes(countryOrState)) {
        const unitedStatesName = i18nCountries.getName('US', 'en');
        dispatch(updateSignUpData({ signUpUSState: countryOrState, signUpCountry: unitedStatesName }));
      } else {
        dispatch(updateSignUpData({ signUpCountry: countryOrState }));
      }

      if (isUserAuthWithEmail && authUser) {
        try {
          setIsLoading(true);
          await linkUser(authUser, AuthProvidersEnum.EMAIL);
          await dispatch(loadUserThunk(authUser.uid)).unwrap();
          history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
        } catch (e) {
          logError(e);
        } finally {
          setIsLoading(false);
        }
        return;
      }
      history.push(PublicRoutes.PASSWORD);
    },
    [history, dispatch, trackPredefinedEvent, authState, authUser]
  );

  return (
    <IonPage>
      <AuthPageHeaderFeature backButtonLink={PublicRoutes.DOB_SIGN_UP} />
      <TranslatedSelectWithSearchFormFeature
        title={t('auth.country_of_living.title')}
        subTitle={t('auth.country_of_living.subtitle')}
        items={Object.entries(i18nCountries.getNames('en', { select: 'official' }))
          .map(([_, name]) => name)
          .filter((name) => (language === 'en' ? name !== 'United States of America' : true))}
        defaultItems={defaultCountries}
        selectedItems={authState.signUpCountry ? new Set([authState.signUpCountry]) : undefined}
        onChange={handleCountrySubmit}
        otherText={t('auth.country_of_living.other')}
        buttonLabel={isUserAuthWithEmail ? t('auth.enter.password.button') : t('auth.button.next')}
        isLoading={isLoading}
        enableSearch={language === 'en'}
        searchPlaceholder={'Search'}
      />
    </IonPage>
  );
};

export default CountryPage;
